import { useCallback, useEffect, useRef, useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Loader2, Wrench } from "lucide-react";
import { useBranding, useKioskSettings, useSolutions, useIndustriesList, useJourney, useContact,
  startSession, endSession, logEvent } from "@/lib/kioskData";
import { KioskCtx } from "./ui";
import { WelcomeScreen, SolutionsScreen, SolutionDetail } from "./screens/welcome_solutions";
import { VideoScreen, PdfScreen, EmailPdfScreen } from "./screens/media";
import { IndustriesScreen, IndustryDetail, JourneyScreen } from "./screens/industry_journey";
import { ContactScreen, ThankYouScreen, ClosingScreen, IdleOverlay } from "./screens/closing";

const HOME = { name: "welcome", params: {} };

export default function KioskApp() {
  const branding = useBranding();
  const kioskCfg = useKioskSettings();
  const solutions = useSolutions();
  const industries = useIndustriesList();
  const journey = useJourney();
  const contact = useContact();

  const [stack, setStack] = useState([HOME]);
  const current = stack[stack.length - 1];

  const [suppressIdle, setSuppressIdle] = useState(false);
  const [idleCount, setIdleCount] = useState(null); // null = not counting down
  const lastActivity = useRef(Date.now());
  const sessionActive = useRef(false);

  const cfg = kioskCfg.data || {};

  const nav = useCallback((name, params = {}) => {
    lastActivity.current = Date.now();
    setIdleCount(null);
    setStack((s) => [...s, { name, params }]);
  }, []);

  const back = useCallback(() => {
    lastActivity.current = Date.now();
    setIdleCount(null);
    setStack((s) => (s.length > 1 ? s.slice(0, -1) : s));
  }, []);

  const startExperience = useCallback(() => {
    if (!sessionActive.current) {
      sessionActive.current = true;
      startSession().then(() => logEvent("session_started"));
    }
  }, []);

  const goHome = useCallback((completed = true) => {
    if (sessionActive.current) {
      logEvent(completed ? "session_completed" : "session_timeout");
      endSession(completed);
      sessionActive.current = false;
    }
    setIdleCount(null);
    lastActivity.current = Date.now();
    setStack([HOME]);
  }, []);

  // activity tracking
  useEffect(() => {
    const mark = () => { lastActivity.current = Date.now(); if (idleCount !== null) setIdleCount(null); };
    const events = ["pointerdown", "touchstart", "keydown"];
    events.forEach((e) => window.addEventListener(e, mark, { passive: true }));
    return () => events.forEach((e) => window.removeEventListener(e, mark));
  }, [idleCount]);

  // idle timer
  useEffect(() => {
    const enabled = cfg.auto_reset_enabled;
    const timeout = cfg.idle_timeout_seconds || 60;
    const countdown = cfg.countdown_seconds || 30;
    const onAttract = current.name === "welcome" || current.name === "closing";

    const tick = setInterval(() => {
      if (!enabled || onAttract || suppressIdle) return;
      const elapsed = (Date.now() - lastActivity.current) / 1000;
      if (idleCount === null) {
        if (elapsed >= timeout) setIdleCount(countdown);
      } else {
        setIdleCount((c) => {
          if (c <= 1) { goHome(false); return null; }
          return c - 1;
        });
      }
    }, 1000);
    return () => clearInterval(tick);
  }, [cfg, current.name, suppressIdle, idleCount, goHome]);

  const continueSession = () => { lastActivity.current = Date.now(); setIdleCount(null); };

  const loading = branding.isLoading || kioskCfg.isLoading || solutions.isLoading;

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center kiosk-radial">
        <Loader2 className="h-10 w-10 animate-spin text-cyan-400" />
      </div>
    );
  }

  if (cfg.maintenance_mode) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 kiosk-radial px-6 text-center" data-testid="kiosk-maintenance">
        <Wrench className="h-14 w-14 text-cyan-400" />
        <h1 className="font-head text-3xl font-bold text-white">Temporarily Unavailable</h1>
        <p className="max-w-md text-slate-400">{cfg.maintenance_message || "Please check back shortly."}</p>
      </div>
    );
  }

  const ctx = {
    branding: branding.data,
    solutions: solutions.data || [],
    industries: industries.data || [],
    journey: journey.data,
    contact: contact.data,
    nav, back, goHome, startExperience, setSuppressIdle,
  };

  const render = () => {
    const { name, params } = current;
    switch (name) {
      case "welcome": return <WelcomeScreen key="welcome" />;
      case "solutions": return <SolutionsScreen key="solutions" />;
      case "solution": return <SolutionDetail key={"sol-" + params.solution.id} {...params} />;
      case "video": return <VideoScreen key="video" {...params} />;
      case "pdf": return <PdfScreen key="pdf" {...params} />;
      case "emailpdf": return <EmailPdfScreen key="emailpdf" {...params} />;
      case "industries": return <IndustriesScreen key="industries" />;
      case "industry": return <IndustryDetail key={"ind-" + params.industry.id} {...params} />;
      case "journey": return <JourneyScreen key="journey" />;
      case "contact": return <ContactScreen key="contact" />;
      case "thankyou": return <ThankYouScreen key="thankyou" />;
      case "closing": return <ClosingScreen key="closing" />;
      default: return <WelcomeScreen key="welcome" />;
    }
  };

  return (
    <KioskCtx.Provider value={ctx}>
      <div className="relative h-[100dvh] overflow-hidden bg-[#050b18] text-white no-select">
        <AnimatePresence mode="wait">{render()}</AnimatePresence>
        {idleCount !== null && (
          <IdleOverlay seconds={idleCount} total={cfg.countdown_seconds || 30} onContinue={continueSession} />
        )}
      </div>
    </KioskCtx.Provider>
  );
}
